import React, { useState } from "react";
import { FiX, FiClipboard, FiCheckCircle } from "react-icons/fi";
import { getOnboardingFormTemplate, getCustomFormStarterSections } from "./boardingFormTemplate";

export default function CreateFormDrawer({
  isOpen,
  onClose,
  onCreateBoardingForm,
  onCreateCustomForm,
  liveBreeds = [],
  onboardingFormExists = false,
}) {
  const [selectedOption, setSelectedOption] = useState("predefined");

  if (!isOpen) return null;

    const handleContinue = () => {
      if (selectedOption === "predefined") {
        if (onboardingFormExists) return;
        const template = getOnboardingFormTemplate(liveBreeds);
        onCreateBoardingForm(template);
        onClose();
      } else if (selectedOption === "custom") {
        onCreateCustomForm({
          formName: "Custom Form",
          formDescription: "",
          formCategory: "General",
          formType: "custom",
          status: "draft",
          formFrequency: "every_time",
          validityPeriod: 12,
          requirement: "optional",
          services: ["General"],
          sections: getCustomFormStarterSections(),
        });
        onClose();
      }
    };

  return (
    <div className="fixed inset-0 z-[65] flex justify-end">
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-[2px]"
        onClick={onClose}
      />

      <div className="relative w-full md:w-[65vw] lg:w-[65vw] max-w-[65vw] bg-white h-full flex flex-col shadow-2xl overflow-hidden border-l border-gray-200">
        <div className="flex items-center justify-between px-8 py-6 border-b border-gray-100 bg-white shrink-0">
          <div>
            <h2 className="text-lg font-bold text-gray-900">
              Start a new form
            </h2>
            <p className="text-xs text-gray-500 mt-0.5">
              Use a template, or create a new form from scratch. Category can be
              changed in the builder.
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-full hover:bg-gray-100 text-gray-400 hover:text-gray-700 transition-colors"
          >
            <FiX className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-8 py-12 flex flex-col justify-between bg-gray-50/50">
          <div className="max-w-4xl w-full mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div
                onClick={() =>
                  !onboardingFormExists && setSelectedOption("predefined")
                }
                onDoubleClick={() => !onboardingFormExists && handleContinue()}
                className={`rounded-2xl p-6 bg-white border-2 transition-all duration-200 flex items-start gap-4 shadow-sm ${
                  onboardingFormExists
                    ? "cursor-not-allowed opacity-60 border-gray-200 bg-gray-50"
                    : `cursor-pointer hover:shadow-md ${
                        selectedOption === "predefined"
                          ? "border-[#1B4582] ring-2 ring-[#1B4582]/15 shadow-md bg-blue-50/20"
                          : "border-gray-200 hover:border-gray-300"
                      }`
                }`}
              >
                <div
                  className={`w-12 h-12 rounded-xl flex items-center justify-center shrink-0 transition-colors ${
                    onboardingFormExists
                      ? "bg-gray-200 text-gray-400"
                      : selectedOption === "predefined"
                        ? "bg-[#1B4582] text-white"
                        : "bg-gray-100 text-gray-700"
                  }`}
                >
                  <FiClipboard className="w-6 h-6" />
                </div>
                <div className="flex-1">
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-400 mb-1">
                    Template
                  </p>
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="text-base font-bold text-gray-900 leading-snug">
                      Customer Onboarding Form
                    </h3>
                    {onboardingFormExists && (
                      <span className="px-2 py-0.5 text-[10px] font-semibold rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
                        Already created
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 mt-2 leading-relaxed">
                    {onboardingFormExists
                      ? "A Customer Onboarding Form already exists. Delete it from the list to create a new one."
                      : "Start from a ready-made onboarding template. You can edit fields after Continue."}
                  </p>
                </div>
              </div>

              <div
                onClick={() => setSelectedOption("custom")}
                onDoubleClick={handleContinue}
                className={`cursor-pointer rounded-2xl p-6 bg-white border-2 transition-all duration-200 flex items-start gap-4 shadow-sm hover:shadow-md ${
                  selectedOption === "custom"
                    ? "border-[#1B4582] ring-2 ring-[#1B4582]/15 shadow-md bg-blue-50/20"
                    : "border-gray-200 hover:border-gray-300"
                }`}
              >
                <div
                  className={`w-12 h-12 rounded-xl flex items-center justify-center shrink-0 transition-colors ${
                    selectedOption === "custom"
                      ? "bg-[#1B4582] text-white"
                      : "bg-gray-100 text-gray-700"
                  }`}
                >
                  <FiCheckCircle className="w-6 h-6" />
                </div>
                <div className="flex-1">
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-400 mb-1">
                    New form
                  </p>
                  <h3 className="text-base font-bold text-gray-900 leading-snug">
                    Create new form
                  </h3>
                  <p className="text-xs text-gray-500 mt-2 leading-relaxed">
                    Build your own form from scratch — add sections and fields
                    in the builder.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="max-w-4xl w-full mx-auto pt-8 border-t border-gray-200/80 flex justify-end">
            <button
              onClick={handleContinue}
              disabled={
                !selectedOption ||
                (selectedOption === "predefined" && onboardingFormExists)
              }
              className={`px-8 py-3 rounded-xl text-sm font-bold transition-all shadow-md ${
                selectedOption
                  ? "bg-[#1B4582] text-white hover:bg-[#163a6e] cursor-pointer"
                  : "bg-blue-200 text-white cursor-not-allowed"
              }`}
            >
              Continue
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
